/**
 * FarmRoadPDFReport
 *
 * Composant PDF pour générer des rapports d'analyse agricole professionnels
 * Utilise @react-pdf/renderer pour créer des PDFs de qualité
 */

import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// Définition des styles pour le PDF
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 11,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff'
  },

  // En-tête amélioré
  header: {
    marginBottom: 20,
    backgroundColor: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
    padding: 15,
    borderRadius: 6
  },
  headerBanner: {
    backgroundColor: '#3b82f6',
    padding: 15,
    marginBottom: 15,
    borderRadius: 6,
    borderLeft: '6pt solid #1e40af'
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 6
  },
  subtitle: {
    fontSize: 11,
    color: '#dbeafe',
    marginBottom: 4
  },
  generatedDate: {
    fontSize: 9,
    color: '#dbeafe',
    fontStyle: 'italic'
  },

  // Métadonnées
  metadataGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
    backgroundColor: '#f8fafc',
    padding: 14,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderStyle: 'solid'
  },
  metadataColumn: {
    flex: 1,
    paddingHorizontal: 8,
    borderRight: '1pt solid #e2e8f0',
    borderStyle: 'solid'
  },
  metadataColumnLast: {
    flex: 1,
    paddingHorizontal: 8
  },
  metadataLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#6b7280',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  metadataValue: {
    fontSize: 11,
    color: '#1f2937',
    fontWeight: 'bold',
    lineHeight: 1.4
  },

  // Badge de statut
  statusBadge: {
    padding: 12,
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 'bold',
    textAlign: 'center',
    marginVertical: 15,
    borderWidth: 1,
    borderStyle: 'solid',
    borderLeft: '4pt solid'
  },
  statusOptimal: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    borderColor: '#10b981'
  },
  statusWarning: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    borderColor: '#f59e0b'
  },
  statusCritical: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderColor: '#dc2626'
  },
  statusNormal: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    borderColor: '#3b82f6'
  },

  // Indicateurs 24h - Regroupement thématique
  thematicGroup: {
    marginBottom: 12
  },
  thematicGroupTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 6,
    paddingLeft: 4,
    paddingBottom: 3,
    borderBottom: '1pt solid #e5e7eb',
    borderStyle: 'solid'
  },
  // Bloc indicateurs intégrés dans sections thématiques
  thematicIndicatorsBlock: {
    marginTop: 12,
    padding: 10,
    backgroundColor: '#f9fafb',
    borderRadius: 6,
    borderLeft: '3pt solid #94a3b8',
    borderStyle: 'solid'
  },
  thematicIndicatorsTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#64748b',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  indicators24hGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6
  },
  indicator24hCard: {
    width: '48%',
    padding: 8,
    borderRadius: 4,
    marginBottom: 6,
    borderLeft: '3pt solid',
    borderStyle: 'solid'
  },
  indicator24hName: {
    fontSize: 8,
    fontWeight: 'bold',
    marginBottom: 3
  },
  indicator24hValue: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 2
  },
  indicator24hUnit: {
    fontSize: 7,
    opacity: 0.8
  },
  indicator24hStatus: {
    fontSize: 7,
    fontWeight: 'bold',
    marginTop: 2,
    textTransform: 'uppercase'
  },
  indicator24hOptimal: {
    fontSize: 7,
    opacity: 0.7,
    marginTop: 3,
    color: '#6b7280'
  },

  // Sections
  section: {
    marginTop: 20,
    marginBottom: 18
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 10,
    backgroundColor: '#f8fafc',
    padding: 10,
    borderLeft: '4pt solid #3b82f6',
    borderRadius: 4,
    borderBottom: '1pt solid #e2e8f0',
    borderStyle: 'solid'
  },
  sectionContent: {
    fontSize: 11,
    lineHeight: 1.8,
    color: '#374151',
    textAlign: 'justify',
    paddingHorizontal: 8,
    marginBottom: 4
  },
  subheading: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1f2937',
    marginTop: 10,
    marginBottom: 6,
    paddingLeft: 4,
    borderLeft: '2pt solid #94a3b8',
    borderStyle: 'solid'
  },

  // Nouvel en-tête compact
  compactHeaderBar: {
    backgroundColor: '#1e293b',
    padding: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 0
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#ffffff'
  },
  headerMeta: {
    fontSize: 11,
    color: '#94a3b8'
  },
  headerSeparator: {
    color: '#64748b',
    fontSize: 11,
    marginHorizontal: 6
  },
  headerRight: {
    fontSize: 9,
    color: '#64748b',
    fontStyle: 'italic'
  },

  // Hero Panel - Dashboard de performance
  heroPanel: {
    backgroundColor: '#f8fafc',
    padding: 20,
    marginTop: 12,
    marginBottom: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#cbd5e1',
    borderStyle: 'solid'
  },
  heroGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between'
  },
  heroCard: {
    width: '31%',
    padding: 12,
    backgroundColor: '#ffffff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderStyle: 'solid',
    alignItems: 'center',
    minHeight: 85
  },
  heroCardLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#64748b',
    textTransform: 'uppercase',
    marginBottom: 6,
    textAlign: 'center'
  },
  heroCardValue: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 2,
    textAlign: 'center'
  },
  heroCardUnit: {
    fontSize: 8,
    color: '#6b7280',
    marginBottom: 6,
    textAlign: 'center'
  },
  heroCardStatus: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 3,
    textAlign: 'center'
  },
  heroCardOptimal: {
    fontSize: 7,
    color: '#9ca3af',
    textAlign: 'center'
  },

  // Section statistiques de l'analyse
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
    gap: 10
  },
  statCard: {
    flex: 1,
    padding: 10,
    backgroundColor: '#f8fafc',
    borderRadius: 4,
    borderLeft: '3pt solid #3b82f6',
    borderStyle: 'solid'
  },
  statLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#6b7280',
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  statValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1f2937'
  },

  // Section métadonnées 2 colonnes
  metadataSection: {
    flexDirection: 'row',
    gap: 20,
    padding: 15,
    backgroundColor: '#f8fafc',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderStyle: 'solid',
    marginBottom: 20
  },
  metadataColumn: {
    flex: 1
  },
  metadataColumnLeft: {
    flex: 0.4
  },
  metadataColumnRight: {
    flex: 0.6
  },
  metadataLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#64748b',
    textTransform: 'uppercase',
    marginBottom: 6,
    letterSpacing: 0.5
  },
  metadataValue: {
    fontSize: 10,
    color: '#475569',
    lineHeight: 1.5,
    marginBottom: 3
  },
  metadataValueBold: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 4
  },
  metadataObjective: {
    fontSize: 10,
    color: '#475569',
    lineHeight: 1.6,
    textAlign: 'justify'
  },

  // Table of Contents
  tocSection: {
    backgroundColor: '#f8fafc',
    padding: 15,
    marginTop: 12,
    marginBottom: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderStyle: 'solid'
  },
  tocTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '2pt solid #cbd5e1',
    borderStyle: 'solid'
  },
  tocItem: {
    flexDirection: 'row',
    paddingVertical: 5,
    paddingLeft: 10,
    borderLeft: '2pt solid #94a3b8',
    borderStyle: 'solid',
    marginBottom: 4
  },
  tocNumber: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#3b82f6',
    width: 25
  },
  tocLabel: {
    fontSize: 10,
    color: '#475569',
    flex: 1
  },

  // Indicator Explanation Table
  explanationTable: {
    marginTop: 15,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderStyle: 'solid',
    borderRadius: 4
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#1e293b',
    padding: 8,
    borderBottom: '2pt solid #475569',
    borderStyle: 'solid'
  },
  tableHeaderCell: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase',
    padding: 4
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1pt solid #e2e8f0',
    borderStyle: 'solid',
    minHeight: 35
  },
  tableRowAlt: {
    backgroundColor: '#f8fafc'
  },
  tableCell: {
    fontSize: 8,
    color: '#1e293b',
    padding: 6,
    lineHeight: 1.4,
    textAlign: 'left'
  },
  tableCellBold: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#1e293b',
    padding: 6
  },
  // Column widths for explanation table
  colParam: { width: '15%' },
  colDesc: { width: '20%' },
  colMin: { width: '11%' },
  colOptimal: { width: '11%' },
  colMax: { width: '11%' },
  colReco: { width: '32%' },

  // Indicateurs clés
  indicatorsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 15,
    gap: 8
  },
  indicatorCard: {
    width: '48%',
    padding: 10,
    backgroundColor: '#ffffff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderStyle: 'solid',
    marginBottom: 8,
    borderLeft: '3pt solid #3b82f6'
  },
  indicatorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4
  },
  indicatorName: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#374151'
  },
  indicatorBadge: {
    fontSize: 8,
    fontWeight: 'bold',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3
  },
  indicatorValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 2
  },
  indicatorOptimal: {
    fontSize: 8,
    color: '#6b7280',
    marginTop: 2
  },

  // Pied de page
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 8,
    color: '#9ca3af',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    borderTopStyle: 'solid',
    paddingTop: 10
  },

  // Pagination
  pageNumber: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    fontSize: 8,
    color: '#9ca3af'
  }
});

// Nettoyage basique du Markdown pour le PDF
const cleanMarkdownForPDF = (text) => {
  if (!text) return '';
  let t = String(text);
  // Titres (#, ##, ###)
  t = t.replace(/^#{1,6}\s+/gm, '');
  // Gras et italique
  t = t.replace(/\*\*(.*?)\*\*/g, '$1');
  t = t.replace(/\*(.*?)\*/g, '$1');
  t = t.replace(/_(.*?)_/g, '$1');
  // Listes à puces et numérotées
  t = t.replace(/^\s*[-+]\s+/gm, '• ');
  t = t.replace(/^\s*\d+\.\s+/gm, '• ');
  // Code inline / blocs
  t = t.replace(/`{1,3}([^`]+)`{1,3}/g, '$1');
  // Lignes vides multiples
  t = t.replace(/\n{3,}/g, '\n\n');
  return t.trim();
};

const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso || 'N/A'; }
};

const formatCulture = (c) => {
  if (!c) return 'Générique';
  const map = { generic: 'Générique', framboises: 'Framboise', framboise: 'Framboise' };
  const lower = String(c).toLowerCase();
  return map[lower] || (lower.charAt(0).toUpperCase() + lower.slice(1));
};

const formatTitle = (metadata) => {
  const culture = formatCulture(metadata?.culture);
  const farm = metadata?.farmName || '';
  return `Rapport d'analyse agronomique`;
};

const renderRichContent = (text) => {
  if (!text) return null;
  const lines = String(text).split(/\r?\n/);
  const blocks = [];
  let para = [];
  const pushPara = () => {
    if (para.length) {
      blocks.push(<Text key={`p-${blocks.length}`} style={styles.sectionContent}>{para.join(' ')}</Text>);
      para = [];
    }
  };

  lines.forEach((l) => {
    // Détecter les titres markdown ## et ###
    const headingMatch = l.match(/^\s*(#{2,3})\s+(.+)$/);
    if (headingMatch) {
      pushPara();
      const headingText = headingMatch[2].trim();
      blocks.push(<Text key={`h-${blocks.length}`} style={styles.subheading}>{headingText}</Text>);
      return;
    }

    // Ligne vide = nouveau paragraphe
    if (/^\s*$/.test(l)) {
      pushPara();
    } else {
      // Nettoyer le markdown et ajouter à paragraph
      let t = l.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1').replace(/`([^`]+)`/g, '$1');
      t = t.replace(/^\s*[-+•]\s+/, '• ');
      para.push(t.trim());
    }
  });
  pushPara();
  return <View>{blocks}</View>;
};

const FarmRoadPDFReport = ({ analysisResult }) => {
  if (!analysisResult || !analysisResult.success) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>Erreur: Aucune donnée d'analyse disponible</Text>
        </Page>
      </Document>
    );
  }

  const { metadata, data, analysis } = analysisResult;
  const startStr = formatDateTime(metadata?.period?.start);
  const endStr = formatDateTime(metadata?.period?.end);

  // Calculer la durée en heures
  const durationInHours = metadata?.period?.start && metadata?.period?.end
    ? Math.round((new Date(metadata.period.end) - new Date(metadata.period.start)) / (1000 * 60 * 60))
    : 0;

  // Fonction pour obtenir le style du badge de statut
  const getStatusStyle = (status) => {
    switch(status) {
      case 'optimal':
        return styles.statusOptimal;
      case 'warning':
        return styles.statusWarning;
      case 'critical':
        return styles.statusCritical;
      default:
        return styles.statusNormal;
    }
  };

  // Fonction pour obtenir le label du statut global (cohérent avec l'interface)
  const getStatusLabel = (status) => {
    switch(status) {
      case 'optimal':
        return 'Optimal';
      case 'warning':
        return 'Attention';
      case 'critical':
        return 'Critique';
      default:
        return 'Inconnu';
    }
  };

  // Fonction pour obtenir le style du badge d'indicateur
  const getIndicatorBadgeStyle = (severity) => {
    const baseStyle = { ...styles.indicatorBadge };
    switch(severity) {
      case 'critical':
        return { ...baseStyle, backgroundColor: '#fee2e2', color: '#991b1b' };
      case 'warning':
        return { ...baseStyle, backgroundColor: '#fef3c7', color: '#92400e' };
      case 'optimal':
        return { ...baseStyle, backgroundColor: '#d1fae5', color: '#065f46' };
      case 'acceptable':
        return { ...baseStyle, backgroundColor: '#e0e7ff', color: '#3730a3' };
      default:
        return { ...baseStyle, backgroundColor: '#f3f4f6', color: '#6b7280' };
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'critical':
        return 'CRITIQUE';
      case 'warning':
        return 'AVERTISSEMENT';
      case 'optimal':
        return 'OPTIMAL';
      case 'acceptable':
        return 'ACCEPTABLE';
      default:
        return 'INCONNU';
    }
  };

  // Formater le nom d'indicateur
  const formatIndicatorName = (name) => {
    return name
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Liste blanche des indicateurs agronomiques clés
  const KEY_INDICATORS = [
    'dli',
    'vpd_diurnal',
    'temperature_heat_stress',
    'temperature_cold_stress',
    'co2_saturation',
    'co2_diurnal',
    'soil_moisture',
    'temperature_diurnal',
    'temperature_nocturnal'
  ];

  // Extraire les indicateurs 24h regroupés par thématique agronomique
  const get24hIndicatorsByTheme = () => {
    if (!data?.dailyIndicators || !data?.dailyEvaluation?.byIndicator) return [];

    const themes = [
      {
        title: '☀️ Lumière & Photosynthèse',
        indicators: [
          {
            key: 'dli',
            name: 'DLI (Cumul journalier)',
            value: data.dailyIndicators.light?.dli,
            unit: 'mol/m²/j',
            severity: data.dailyEvaluation.byIndicator.dli?.severity,
            optimal_min: data.dailyEvaluation.byIndicator.dli?.optimal_min,
            optimal_max: data.dailyEvaluation.byIndicator.dli?.optimal_max,
            deviation: data.dailyEvaluation.byIndicator.dli?.deviation
          },
          {
            key: 'sunlight_duration',
            name: "Durée d'ensoleillement",
            value: data.dailyIndicators.light?.sunlightDuration,
            unit: 'heures',
            severity: data.dailyEvaluation.byIndicator.sunlight_duration?.severity,
            optimal_min: data.dailyEvaluation.byIndicator.sunlight_duration?.optimal_min,
            optimal_max: data.dailyEvaluation.byIndicator.sunlight_duration?.optimal_max,
            deviation: data.dailyEvaluation.byIndicator.sunlight_duration?.deviation
          },
          {
            key: 'radiation_mean_diurnal',
            name: 'Radiation moyenne diurne',
            value: data.dailyIndicators.light?.meanDiurnalRadiation,
            unit: 'µmol/m²/s',
            severity: data.dailyEvaluation.byIndicator.radiation_mean_diurnal?.severity,
            optimal_min: data.dailyEvaluation.byIndicator.radiation_mean_diurnal?.optimal_min,
            optimal_max: data.dailyEvaluation.byIndicator.radiation_mean_diurnal?.optimal_max,
            deviation: data.dailyEvaluation.byIndicator.radiation_mean_diurnal?.deviation
          }
        ]
      },
      {
        title: '🌡️ Climat (VPD & Température)',
        indicators: [
          {
            key: 'vpd_diurnal',
            name: 'VPD diurne (Transpiration)',
            value: data.dailyIndicators.vpd?.diurnalMean,
            unit: 'kPa',
            severity: data.dailyEvaluation.byIndicator.vpd_diurnal?.severity,
            optimal_min: data.dailyEvaluation.byIndicator.vpd_diurnal?.optimal_min,
            optimal_max: data.dailyEvaluation.byIndicator.vpd_diurnal?.optimal_max,
            deviation: data.dailyEvaluation.byIndicator.vpd_diurnal?.deviation
          },
          {
            key: 'temperature_heat_stress',
            name: 'Stress chaud (>30°C)',
            value: data.dailyIndicators.temperature?.heatStressHours,
            unit: 'heures',
            severity: data.dailyEvaluation.byIndicator.temperature_heat_stress?.severity,
            optimal_min: data.dailyEvaluation.byIndicator.temperature_heat_stress?.optimal_min,
            optimal_max: data.dailyEvaluation.byIndicator.temperature_heat_stress?.optimal_max,
            deviation: data.dailyEvaluation.byIndicator.temperature_heat_stress?.deviation
          },
          {
            key: 'temperature_cold_stress',
            name: 'Stress froid (<10°C)',
            value: data.dailyIndicators.temperature?.coldStressHours,
            unit: 'heures',
            severity: data.dailyEvaluation.byIndicator.temperature_cold_stress?.severity,
            optimal_min: data.dailyEvaluation.byIndicator.temperature_cold_stress?.optimal_min,
            optimal_max: data.dailyEvaluation.byIndicator.temperature_cold_stress?.optimal_max,
            deviation: data.dailyEvaluation.byIndicator.temperature_cold_stress?.deviation
          },
          {
            key: 'temperature_amplitude',
            name: 'Amplitude thermique jour/nuit',
            value: data.dailyIndicators.temperature?.amplitude,
            unit: '°C',
            severity: data.dailyEvaluation.byIndicator.temperature_amplitude?.severity,
            optimal_min: data.dailyEvaluation.byIndicator.temperature_amplitude?.optimal_min,
            optimal_max: data.dailyEvaluation.byIndicator.temperature_amplitude?.optimal_max,
            deviation: data.dailyEvaluation.byIndicator.temperature_amplitude?.deviation
          }
        ]
      },
      {
        title: '💨 CO2 & Photosynthèse',
        indicators: [
          {
            key: 'co2_diurnal',
            name: 'CO2 diurne moyen',
            value: data.dailyIndicators.co2?.diurnalMean,
            unit: 'ppm',
            severity: data.dailyEvaluation.byIndicator.co2_diurnal?.severity,
            optimal_min: data.dailyEvaluation.byIndicator.co2_diurnal?.optimal_min,
            optimal_max: data.dailyEvaluation.byIndicator.co2_diurnal?.optimal_max,
            deviation: data.dailyEvaluation.byIndicator.co2_diurnal?.deviation
          },
          {
            key: 'co2_saturation',
            name: 'Saturation CO2 (>1400 ppm)',
            value: data.dailyIndicators.co2?.saturationHours,
            unit: 'heures',
            severity: data.dailyEvaluation.byIndicator.co2_saturation?.severity,
            optimal_min: data.dailyEvaluation.byIndicator.co2_saturation?.optimal_min,
            optimal_max: data.dailyEvaluation.byIndicator.co2_saturation?.optimal_max,
            deviation: data.dailyEvaluation.byIndicator.co2_saturation?.deviation
          }
        ]
      },
      {
        title: '💧 Humidité & Irrigation',
        indicators: [
          {
            key: 'soil_moisture',
            name: 'Humidité substrat moyenne',
            value: data.dailyIndicators.humidity?.mean24h,
            unit: '%',
            severity: data.dailyEvaluation.byIndicator.soil_moisture?.severity,
            optimal_min: data.dailyEvaluation.byIndicator.soil_moisture?.optimal_min,
            optimal_max: data.dailyEvaluation.byIndicator.soil_moisture?.optimal_max,
            deviation: data.dailyEvaluation.byIndicator.soil_moisture?.deviation
          },
          {
            key: 'humidity',
            name: 'Humidité relative moyenne',
            value: data.dailyIndicators.airHumidity?.mean24h,
            unit: '%',
            severity: data.dailyEvaluation.byIndicator.humidity?.severity,
            optimal_min: data.dailyEvaluation.byIndicator.humidity?.optimal_min,
            optimal_max: data.dailyEvaluation.byIndicator.humidity?.optimal_max,
            deviation: data.dailyEvaluation.byIndicator.humidity?.deviation
          }
        ]
      }
    ];

    // Filtrer les indicateurs avec valeurs valides pour chaque thème
    return themes.map(theme => ({
      ...theme,
      indicators: theme.indicators.filter(ind => ind.value != null && !isNaN(ind.value))
    })).filter(theme => theme.indicators.length > 0);
  };

  // Extraire les indicateurs les plus importants (critiques et warnings) - FILTRÉS
  const getTopIndicators = () => {
    // Essayer d'abord dailyEvaluation, puis thresholdsEvaluation
    const evalSource = data?.dailyEvaluation?.byIndicator || data?.thresholdsEvaluation?.byIndicator;
    if (!evalSource) return [];

    const indicators = Object.entries(evalSource)
      .filter(([name]) => KEY_INDICATORS.includes(name)) // FILTRER uniquement indicateurs clés
      .map(([name, indicatorData]) => ({
        name,
        ...indicatorData,
        // Normaliser les valeurs optimales (extraire du nested threshold si présent)
        optimal_min: indicatorData.optimal_min ?? indicatorData.threshold?.optimal_min,
        optimal_max: indicatorData.optimal_max ?? indicatorData.threshold?.optimal_max,
        unit: indicatorData.unit || indicatorData.threshold?.unit || '',
        latestValue: indicatorData.value ?? indicatorData.latestValue,
        priority: indicatorData.severity === 'critical' ? 3 : indicatorData.severity === 'warning' ? 2 : 1
      }));

    indicators.sort((a, b) => b.priority - a.priority);
    return indicators.slice(0, 6); // Top 6 indicateurs pour remplir le Hero Panel
  };

  const indicatorThemes = get24hIndicatorsByTheme();
  const topIndicators = getTopIndicators();

  // Liste des indicateurs à afficher dans le tableau de référence (exclusions: gdd, temperature_diurnal, temperature_nocturnal)
  const TABLE_INDICATORS_ORDER = [
    'dli',
    'vpd_diurnal',
    'temperature_heat_stress',
    'temperature_cold_stress',
    'temperature_amplitude',
    'co2_diurnal',
    'co2_saturation',
    'radiation_mean_diurnal',
    'sunlight_duration',
    'soil_moisture',
    'humidity'
  ];

  // Mapping des noms d'affichage et recommandations génériques
  const INDICATOR_DISPLAY_INFO = {
    dli: {
      displayName: 'DLI',
      recommendations: (threshold) => `Si <${threshold.min_warning} : retirer ombrage, nettoyer plastique de serre. Si >${threshold.max_warning} : appliquer ombrage 20-30% pour limiter stress lumineux.`
    },
    vpd_diurnal: {
      displayName: 'VPD Diurnal',
      recommendations: (threshold) => `Si <${threshold.optimal_min} : ventiler pour assécher air (prévention maladies). Si >${threshold.max_warning} : augmenter irrigation + brumisation légère.`
    },
    temperature_heat_stress: {
      displayName: 'Stress Chaud',
      recommendations: (threshold) => `Si >${threshold.max_warning}h à 30°C : ventilation maximale + ombrage 30% + brumisation immédiate. Photosynthèse bloquée au-delà.`
    },
    temperature_cold_stress: {
      displayName: 'Stress Froid',
      recommendations: (threshold) => `Si >${threshold.max_warning}h <10°C : activer chauffage immédiatement. Fermer ouvertures latérales. Surveiller température nocturne et matinale.`
    },
    temperature_amplitude: {
      displayName: 'Amplitude Thermique',
      recommendations: (threshold) => `Si <${threshold.optimal_min}°C : augmenter ventilation diurne pour créer écart. Si >${threshold.max_warning}°C : réguler chauffage nocturne.`
    },
    co2_diurnal: {
      displayName: 'CO2 Diurnal',
      recommendations: (threshold) => `Si <${threshold.min_warning} : enrichissement CO2 recommandé (photosynthèse limitée). Si >${threshold.max_warning} : ventiler pour évacuer excès.`
    },
    co2_saturation: {
      displayName: 'Saturation CO2',
      recommendations: (threshold) => `Si >${threshold.max_warning}h : ventilation immédiate 30-45 min (photosynthèse inefficace au-delà 1400 ppm).`
    },
    radiation_mean_diurnal: {
      displayName: 'Radiation PAR Diurne',
      recommendations: (threshold) => `Si <${threshold.min_warning} : nettoyer plastique + retirer ombrage. Si >${threshold.max_warning} : ombrage 15-25% pour limiter photoinhibition.`
    },
    sunlight_duration: {
      displayName: 'Durée Ensoleillement',
      recommendations: (threshold) => `Si <${threshold.min_warning}h : compléter avec éclairage LED horticole. Si >${threshold.max_warning}h : prévoir obscurcissement partiel.`
    },
    soil_moisture: {
      displayName: 'Humidité Substrat',
      recommendations: (threshold) => `Si <${threshold.optimal_min}% : irrigation immédiate (stress hydrique). Si >${threshold.max_warning}% : espacer arrosages (risque asphyxie racinaire).`
    },
    humidity: {
      displayName: 'Humidité Relative',
      recommendations: (threshold) => `Si <${threshold.optimal_min}% : brumisation légère. Si nuit >${threshold.max_warning}% : ventilation nocturne 30 min (prévention maladies).`
    }
  };

  // Fonction pour extraire les seuils du tableau depuis les données de l'analyse
  const getTableIndicators = () => {
    const evalSource = data?.dailyEvaluation?.byIndicator || {};
    const indicators = [];

    TABLE_INDICATORS_ORDER.forEach(key => {
      const indicatorData = evalSource[key];
      if (!indicatorData) return;

      const threshold = indicatorData.threshold || indicatorData;
      const displayInfo = INDICATOR_DISPLAY_INFO[key];

      if (!threshold || !displayInfo) return;

      indicators.push({
        key,
        displayName: displayInfo.displayName,
        description: threshold.description || 'N/A',
        minCritical: threshold.min_critical != null ? `< ${threshold.min_critical}` : 'N/A',
        optimal: threshold.optimal_min != null && threshold.optimal_max != null
          ? `${threshold.optimal_min}-${threshold.optimal_max}`
          : 'N/A',
        maxCritical: threshold.max_critical != null ? `> ${threshold.max_critical}` : 'N/A',
        unit: threshold.unit || '',
        recommendations: displayInfo.recommendations(threshold)
      });
    });

    return indicators;
  };

  const tableIndicators = getTableIndicators();

  // Fonction helper pour obtenir les couleurs selon la sévérité
  const get24hCardColor = (severity) => {
    switch(severity) {
      case 'critical':
        return { bg: '#fee2e2', border: '#dc2626', text: '#991b1b' };
      case 'warning':
        return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };
      case 'optimal':
        return { bg: '#d1fae5', border: '#10b981', text: '#065f46' };
      case 'acceptable':
      default: // acceptable
        return { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' };
    }
  };

  // Fonction pour obtenir les indicateurs d'un thème spécifique
  const getIndicatorsByThemeName = (themeName) => {
    const theme = indicatorThemes.find(t => t.title.includes(themeName));
    return theme ? theme.indicators : [];
  };

  // Fonction pour rendre une section thématique avec ses indicateurs
  const renderThematicSectionWithIndicators = (title, content, themeName) => {
    if (!content) return null;

    const themeIndicators = getIndicatorsByThemeName(themeName);

    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{title}</Text>
        {renderRichContent(content)}

        {/* Indicateurs pertinents pour ce thème */}
        {themeIndicators.length > 0 && (
          <View style={styles.thematicIndicatorsBlock}>
            <Text style={styles.thematicIndicatorsTitle}>
              Indicateurs mesurés
            </Text>
            <View style={styles.indicators24hGrid}>
              {themeIndicators.map((indicator, index) => {
                const colors = get24hCardColor(indicator.severity);
                return (
                  <View key={index} style={[
                    styles.indicator24hCard,
                    {
                      backgroundColor: colors.bg,
                      borderLeftColor: colors.border
                    }
                  ]}>
                    <Text style={styles.indicator24hName}>{indicator.name}</Text>
                    <Text style={[styles.indicator24hValue, { color: colors.text }]}>
                      {indicator.value.toFixed(indicator.key === 'dli' || indicator.key === 'vpd_diurnal' ? 2 : 1)}
                    </Text>
                    <Text style={styles.indicator24hUnit}>{indicator.unit}</Text>
                    <Text style={[styles.indicator24hStatus, { color: colors.text }]}>
                      {indicator.severity || 'acceptable'}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* SECTION 1: Barre d'en-tête compacte */}
        <View style={styles.compactHeaderBar}>
          <View style={styles.headerLeft}>
            <Text style={styles.headerTitle}>◆ Rapport Agronomique</Text>
            <Text style={styles.headerSeparator}>|</Text>
            <Text style={styles.headerMeta}>{metadata.farmName || 'Ferme'}</Text>
            <Text style={styles.headerSeparator}>•</Text>
            <Text style={styles.headerMeta}>{formatCulture(metadata.culture)}</Text>
          </View>
          <View>
            <Text style={styles.headerRight}>
              Généré le {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })} à {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </View>
        </View>

        {/* TABLE DES MATIÈRES */}
        <View style={styles.tocSection}>
          <Text style={styles.tocTitle}>Table des matières</Text>
          <View style={styles.tocItem}>
            <Text style={styles.tocNumber}>1.</Text>
            <Text style={styles.tocLabel}>Diagnostic Global</Text>
          </View>
          <View style={styles.tocItem}>
            <Text style={styles.tocNumber}>2.</Text>
            <Text style={styles.tocLabel}>Indicateurs Agronomiques 24h</Text>
          </View>
          <View style={styles.tocItem}>
            <Text style={styles.tocNumber}>3.</Text>
            <Text style={styles.tocLabel}>Analyse Détaillée</Text>
          </View>
          <View style={styles.tocItem}>
            <Text style={styles.tocNumber}>4.</Text>
            <Text style={styles.tocLabel}>Recommandations</Text>
          </View>
          <View style={styles.tocItem}>
            <Text style={styles.tocNumber}>5.</Text>
            <Text style={styles.tocLabel}>Alertes et Prédictions</Text>
          </View>
          <View style={styles.tocItem}>
            <Text style={styles.tocNumber}>6.</Text>
            <Text style={styles.tocLabel}>Guide des Indicateurs et Recommandations</Text>
          </View>
        </View>

        {/* SECTION 2: Métadonnées (Période + Objectif) */}
        <View style={styles.metadataSection}>
          <View style={styles.metadataColumnLeft}>
            <Text style={styles.metadataLabel}>PÉRIODE D'ANALYSE</Text>
            <Text style={styles.metadataValue}>Du {startStr}</Text>
            <Text style={styles.metadataValue}>au {endStr}</Text>
          </View>
          <View style={styles.metadataColumnRight}>
            <Text style={styles.metadataLabel}>OBJECTIF AGRICOLE</Text>
            <Text style={styles.metadataObjective}>
              {metadata.objective || 'Optimiser les conditions de culture pour maximiser le rendement et la qualité de la production.'}
            </Text>
          </View>
        </View>

        {/* Statistiques de l'analyse */}
        <View style={styles.statsRow}>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>MESURES ANALYSÉES</Text>
            <Text style={styles.statValue}>{analysisResult.measurementsCount || 0}</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>INDICATEURS</Text>
            <Text style={styles.statValue}>{indicatorThemes.flatMap(t => t.indicators).length}</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>DURÉE</Text>
            <Text style={styles.statValue}>{durationInHours} h</Text>
          </View>
        </View>

        {/* Section Diagnostic */}
        {analysis.parsed?.sections?.diagnostic && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Diagnostic Global</Text>
            {renderRichContent(analysis.parsed.sections.diagnostic)}
          </View>
        )}

        {/* Indicateurs Agronomiques 24h - Nouvelle page dédiée */}
        {indicatorThemes.length > 0 && (
          <View style={styles.section} break>
            <Text style={styles.sectionTitle}>Indicateurs Agronomiques 24h</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
              {indicatorThemes.flatMap(theme => theme.indicators).map((indicator, index) => {
                const colors = get24hCardColor(indicator.severity);
                return (
                  <View key={index} wrap={false} style={{
                    width: '31.5%',
                    padding: 10,
                    borderRadius: 4,
                    marginBottom: 8,
                    backgroundColor: colors.bg,
                    borderLeft: '3pt solid',
                    borderLeftColor: colors.border,
                    borderStyle: 'solid'
                  }}>
                    <Text style={styles.indicator24hName}>{indicator.name}</Text>
                    <Text style={[styles.indicator24hValue, { color: colors.text }]}>
                      {indicator.value.toFixed(indicator.key === 'dli' || indicator.key === 'vpd_diurnal' ? 2 : 1)}
                    </Text>
                    <Text style={styles.indicator24hUnit}>{indicator.unit}</Text>
                    <Text style={[styles.indicator24hStatus, { color: colors.text }]}>
                      {indicator.severity || 'acceptable'}
                    </Text>
                    {/* Zone optimale - affichée sur toutes les cartes */}
                    {indicator.optimal_min != null && indicator.optimal_max != null && (
                      <Text style={styles.indicator24hOptimal}>
                        Optimal: {indicator.optimal_min}-{indicator.optimal_max} {indicator.unit}
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* Section Analyse Détaillée - Nouvelle page */}
        {analysis.parsed?.sections?.detailedAnalysis && (
          <View style={styles.section} break>
            <Text style={styles.sectionTitle}>Analyse Détaillée</Text>
            {renderRichContent(analysis.parsed.sections.detailedAnalysis)}
          </View>
        )}

        {/* Section Recommandations - Nouvelle page */}
        {analysis.parsed?.sections?.recommendations && (
          <View style={styles.section} break>
            <Text style={styles.sectionTitle}>Recommandations</Text>
            {renderRichContent(analysis.parsed.sections.recommendations)}
          </View>
        )}

        {/* Section Prédictions */}
        {analysis.parsed?.sections?.predictions && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Alertes et Prédictions</Text>
            {renderRichContent(analysis.parsed.sections.predictions)}
          </View>
        )}

        {/* Section Références */}
        {analysis.parsed?.sections?.references && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Références</Text>
            {renderRichContent(analysis.parsed.sections.references)}
          </View>
        )}

        {/* GUIDE DES INDICATEURS - Nouvelle page dédiée */}
        <View style={styles.section} break>
          <Text style={styles.sectionTitle}>Guide des Indicateurs et Recommandations</Text>
          <Text style={[styles.sectionContent, { marginBottom: 10 }]}>
            Ce tableau présente les seuils de référence pour chaque indicateur agronomique et les actions recommandées selon les situations.
          </Text>

          <View style={styles.explanationTable}>
            {/* En-tête du tableau */}
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, styles.colParam]}>Paramètre</Text>
              <Text style={[styles.tableHeaderCell, styles.colDesc]}>Description</Text>
              <Text style={[styles.tableHeaderCell, styles.colMin]}>Min Critical</Text>
              <Text style={[styles.tableHeaderCell, styles.colOptimal]}>Optimal</Text>
              <Text style={[styles.tableHeaderCell, styles.colMax]}>Max Critical</Text>
              <Text style={[styles.tableHeaderCell, styles.colReco]}>Recommandations</Text>
            </View>

            {/* Génération dynamique des lignes du tableau */}
            {tableIndicators.map((indicator, index) => (
              <View
                key={indicator.key}
                style={index % 2 === 0 ? [styles.tableRow, styles.tableRowAlt] : styles.tableRow}
              >
                <Text style={[styles.tableCellBold, styles.colParam]}>{indicator.displayName}</Text>
                <Text style={[styles.tableCell, styles.colDesc]}>{indicator.description}</Text>
                <Text style={[styles.tableCell, styles.colMin]}>{indicator.minCritical}</Text>
                <Text style={[styles.tableCell, styles.colOptimal]}>{indicator.optimal}</Text>
                <Text style={[styles.tableCell, styles.colMax]}>{indicator.maxCritical}</Text>
                <Text style={[styles.tableCell, styles.colReco]}>{indicator.recommendations}</Text>
              </View>
            ))}

          </View>

          <Text style={[styles.sectionContent, { marginTop: 15, fontStyle: 'italic', fontSize: 9, color: '#64748b' }]}>
            Note: Ces recommandations sont spécifiques à la culture de framboises en serre.
            Les seuils peuvent varier selon le stade phénologique et les conditions locales.
          </Text>
        </View>

        {/* Pied de page */}
        <View style={styles.footer} fixed>
          <Text>
            Généré le {new Date().toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })} à {new Date().toLocaleTimeString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Text>
          <Text style={{ marginTop: 3 }}>
            Aladinn Agriculture Intelligence Platform • Rapport confidentiel
          </Text>
        </View>

        {/* Numéro de page */}
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
          `Page ${pageNumber} / ${totalPages}`
        )} fixed />
      </Page>
    </Document>
  );
};

export default FarmRoadPDFReport;
